// src/components/admin/JobReviewModal.js
import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Avatar,
  Alert,
  useTheme,
  useMediaQuery,
  Drawer,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import LanguageIcon from "@mui/icons-material/Language";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VideocamIcon from "@mui/icons-material/Videocam";
import PersonIcon from "@mui/icons-material/Person";
import InfoIcon from "@mui/icons-material/Info";
import GavelIcon from "@mui/icons-material/Gavel";
import COLORS from "../../utils/color";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: COLORS.gradients.background,
    borderRadius: "24px",
    width: "95vw",
    maxWidth: "1200px",
    maxHeight: "90vh",
    margin: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      width: "100vw",
      height: "100vh",
      maxWidth: "none",
      maxHeight: "none",
      margin: 0,
      borderRadius: 0,
    },
  },
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    background: COLORS.gradients.background,
    width: "100vw",
    height: "calc(100vh - 64px)", // Account for navbar height
    top: "64px", // Start below navbar
    position: "fixed",
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  height: "85vh",
  [theme.breakpoints.down("md")]: {
    height: "100vh",
  },
}));

const MobileContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "calc(100vh - 64px)", // Account for navbar
  overflow: "hidden",
}));

const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderBottom: `1px solid ${COLORS.border.primary}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: COLORS.background.secondary,
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
  },
}));

const MobileHeader = styled(AppBar)(({ theme }) => ({
  backgroundColor: COLORS.background.secondary,
  borderBottom: `1px solid ${COLORS.border.primary}`,
  boxShadow: "none",
}));

const VideoSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  position: "relative",
  backgroundColor: "#000",
  borderRadius: "12px",
  margin: theme.spacing(3),
  overflow: "hidden",
  minHeight: "400px",
  [theme.breakpoints.down("lg")]: {
    margin: theme.spacing(2),
    minHeight: "300px",
  },
  [theme.breakpoints.down("md")]: {
    margin: 0,
    borderRadius: 0,
    minHeight: "250px",
  },
}));

const VideoElement = styled("video")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
});

const VideoControls = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  background: "linear-gradient(transparent, rgba(0,0,0,0.8))",
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(1.5),
  },
}));

const ControlButton = styled(IconButton)(({ theme }) => ({
  color: "white",
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  padding: theme.spacing(1),
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(0.75),
  },
}));

const InfoSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderTop: `1px solid ${COLORS.border.primary}`,
  background: COLORS.background.secondary,
  overflow: "auto",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
  },
}));

const MobileInfoSection = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: theme.spacing(2),
  background: COLORS.background.secondary,
}));

const ActionSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderTop: `1px solid ${COLORS.border.primary}`,
  background: COLORS.background.secondary,
  display: "flex",
  gap: theme.spacing(2),
  justifyContent: "flex-end",
  flexWrap: "wrap",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
    position: "sticky",
    bottom: 0,
    zIndex: 1,
  },
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: "12px",
  textTransform: "none",
  fontWeight: 600,
  padding: "12px 32px",
  fontSize: "16px",
  minWidth: "120px",
  [theme.breakpoints.down("md")]: {
    padding: "10px 24px",
    fontSize: "14px",
    minWidth: "100px",
    flex: 1,
  },
  ...(variant === "approve" && {
    background: `linear-gradient(135deg, ${COLORS.status.success}, ${COLORS.status.success}DD)`,
    color: "white",
    "&:hover": {
      background: `linear-gradient(135deg, ${COLORS.status.success}CC, ${COLORS.status.success}AA)`,
      transform: "translateY(-1px)",
      boxShadow: `0 6px 20px ${COLORS.status.success}40`,
    },
  }),
  ...(variant === "reject" && {
    background: `linear-gradient(135deg, ${COLORS.status.error}, ${COLORS.status.error}DD)`,
    color: "white",
    "&:hover": {
      background: `linear-gradient(135deg, ${COLORS.status.error}CC, ${COLORS.status.error}AA)`,
      transform: "translateY(-1px)",
      boxShadow: `0 6px 20px ${COLORS.status.error}40`,
    },
  }),
}));

const InfoCard = styled(Box)(({ theme }) => ({
  background: COLORS.overlay.light,
  border: `1px solid ${COLORS.border.primary}`,
  borderRadius: "12px",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("md")]: {
    borderRadius: "8px",
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: COLORS.overlay.light,
    "& fieldset": {
      borderColor: COLORS.border.primary,
    },
    "&:hover fieldset": {
      borderColor: COLORS.border.focus,
    },
    "&.Mui-focused fieldset": {
      borderColor: COLORS.primary.main,
    },
  },
  "& .MuiInputLabel-root": {
    color: COLORS.text.secondary,
  },
  "& .MuiInputBase-input": {
    color: COLORS.text.primary,
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: COLORS.overlay.light,
    "& fieldset": {
      borderColor: COLORS.border.primary,
    },
    "&:hover fieldset": {
      borderColor: COLORS.border.focus,
    },
    "&.Mui-focused fieldset": {
      borderColor: COLORS.primary.main,
    },
  },
  "& .MuiInputLabel-root": {
    color: COLORS.text.secondary,
  },
  "& .MuiSelect-select": {
    color: COLORS.text.primary,
  },
}));

const MobileTabPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  overflow: "auto",
  flex: 1,
}));

const TabLabel = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  textTransform: "none",
}));

const rejectionReasons = [
  "Inappropriate content (violence, adult content)",
  "Copyright infringement suspected",
  "Contains copyrighted music",
  "Unauthorized use of person's likeness",
  "Hate speech or offensive language",
  "Commercial/advertising content",
  "Professional media content (requires licensing)",
  "Poor video quality",
  "Audio quality issues",
  "Custom reason (specify below)",
];

const JobReviewModal = ({
  open,
  onClose,
  job,
  onApprove,
  onReject,
  loading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [reviewAction, setReviewAction] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [customNotes, setCustomNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const videoRef = useRef(null);

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play().catch(console.error);
    }
  };

  const handleMuteToggle = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.requestFullscreen) {
      video.requestFullscreen();
    }
  };

  const handleApprove = async () => {
    if (submitting) return;

    setSubmitting(true);
    try {
      await onApprove(job, customNotes);
      onClose();
    } catch (error) {
      console.error("Approval failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (submitting) return;

    const notes =
      rejectionReason === "Custom reason (specify below)"
        ? customNotes
        : rejectionReason + (customNotes ? ` - ${customNotes}` : "");

    if (!notes.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      await onReject(job, notes);
      onClose();
    } catch (error) {
      console.error("Rejection failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getInitials = (email) => {
    return email ? email.charAt(0).toUpperCase() : "?";
  };

  if (!job) return null;

  // Video Component (shared between mobile and desktop)
  // Simple VideoPlayer component with default controls
  const VideoPlayer = () => (
    <VideoSection>
      {job.video_url ? (
        <VideoElement
          ref={videoRef}
          src={job.video_url}
          controls={true} // Enable default browser controls
          preload="metadata"
          playsInline // Important for mobile browsers
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: COLORS.text.secondary,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <VideocamIcon sx={{ fontSize: { xs: 48, md: 64 }, mb: 2 }} />
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}
            >
              Video not available
            </Typography>
          </Box>
        </Box>
      )}
    </VideoSection>
  );

  const handleClose = () => {
    if (submitting) return;

    // Pause video when closing
    if (videoRef.current) {
      videoRef.current.pause();
    }

    // Reset form
    setReviewAction("");
    setRejectionReason("");
    setCustomNotes("");
    setActiveTab(0);

    onClose();
  };

  // Info Content Component (shared between mobile and desktop)
  const InfoContent = () => (
    <>
      {/* User Info */}
      <InfoCard>
        <Typography
          variant="h6"
          sx={{
            color: COLORS.text.primary,
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: { xs: "1rem", md: "1.25rem" },
          }}
        >
          <PersonIcon /> User Information
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              width: { xs: 40, md: 48 },
              height: { xs: 40, md: 48 },
              backgroundColor: COLORS.primary.main,
            }}
          >
            {getInitials(job.user_email)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body1"
              sx={{
                color: COLORS.text.primary,
                fontWeight: 600,
                fontSize: { xs: "0.875rem", md: "1rem" },
                wordBreak: "break-word",
              }}
            >
              {job.user_email}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: COLORS.text.secondary,
                fontSize: { xs: "0.7rem", md: "0.75rem" },
                wordBreak: "break-all",
              }}
            >
              User ID: {job.user_id}
            </Typography>
          </Box>
        </Box>
      </InfoCard>

      {/* Video Details */}
      <InfoCard>
        <Typography
          variant="h6"
          sx={{
            color: COLORS.text.primary,
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: { xs: "1rem", md: "1.25rem" },
          }}
        >
          <VideocamIcon /> Video Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{
                color: COLORS.text.secondary,
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            >
              Filename
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: COLORS.text.primary,
                fontWeight: 500,
                fontSize: { xs: "0.875rem", md: "1rem" },
                wordBreak: "break-word",
              }}
            >
              {job.video_filename}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body2"
              sx={{
                color: COLORS.text.secondary,
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            >
              File Size
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: COLORS.text.primary,
                fontSize: { xs: "0.875rem", md: "1rem" },
              }}
            >
              {formatFileSize(job.video_file_size)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body2"
              sx={{
                color: COLORS.text.secondary,
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            >
              Speakers
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: COLORS.text.primary,
                fontSize: { xs: "0.875rem", md: "1rem" },
              }}
            >
              {job.num_speakers || "Auto-detect"}
            </Typography>
          </Grid>
        </Grid>
      </InfoCard>

      {/* Dubbing Settings */}
      <InfoCard>
        <Typography
          variant="h6"
          sx={{
            color: COLORS.text.primary,
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: { xs: "1rem", md: "1.25rem" },
          }}
        >
          <LanguageIcon /> Dubbing Settings
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: COLORS.text.secondary,
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                }}
              >
                Languages
              </Typography>
            </Box>
            <Chip
              label={`${job.source_language} → ${job.target_language}`}
              sx={{
                backgroundColor: `${COLORS.secondary.main}20`,
                color: COLORS.secondary.main,
                fontWeight: 600,
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{
                color: COLORS.text.secondary,
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            >
              Options
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
              {job.enable_lipsync && (
                <Chip
                  label="Lip-sync"
                  size="small"
                  sx={{
                    backgroundColor: `${COLORS.status.success}20`,
                    color: COLORS.status.success,
                    fontSize: { xs: "0.65rem", md: "0.75rem" },
                  }}
                />
              )}
              {job.watermark && (
                <Chip
                  label="Watermark"
                  size="small"
                  sx={{
                    backgroundColor: `${COLORS.status.info}20`,
                    color: COLORS.status.info,
                    fontSize: { xs: "0.65rem", md: "0.75rem" },
                  }}
                />
              )}
              {job.drop_background_audio && (
                <Chip
                  label="No Background Audio"
                  size="small"
                  sx={{
                    backgroundColor: `${COLORS.status.warning}20`,
                    color: COLORS.status.warning,
                    fontSize: { xs: "0.65rem", md: "0.75rem" },
                  }}
                />
              )}
              {job.use_profanity_filter && (
                <Chip
                  label="Profanity Filter"
                  size="small"
                  sx={{
                    backgroundColor: `${COLORS.primary.main}20`,
                    color: COLORS.primary.main,
                    fontSize: { xs: "0.65rem", md: "0.75rem" },
                  }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </InfoCard>

      {/* Submission Info */}
      <InfoCard>
        <Typography
          variant="h6"
          sx={{
            color: COLORS.text.primary,
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: { xs: "1rem", md: "1.25rem" },
          }}
        >
          <AccessTimeIcon /> Submission Info
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{
                color: COLORS.text.secondary,
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            >
              Submitted
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: COLORS.text.primary,
                fontSize: { xs: "0.875rem", md: "1rem" },
              }}
            >
              {new Date(job.created_at).toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{
                color: COLORS.text.secondary,
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            >
              Status
            </Typography>
            <Chip
              label={job.approval_status || "Pending"}
              sx={{
                backgroundColor: `${COLORS.status.warning}20`,
                color: COLORS.status.warning,
                fontWeight: 600,
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            />
          </Grid>
        </Grid>
      </InfoCard>
    </>
  );

  // Review Actions Component
  const ReviewActions = () => (
    <Box>
      <Typography
        variant="h6"
        sx={{
          color: COLORS.text.primary,
          mb: 2,
          fontSize: { xs: "1rem", md: "1.25rem" },
        }}
      >
        Review Decision
      </Typography>

      {/* Rejection Reason (only show if rejecting) */}
      {reviewAction === "reject" && (
        <Box sx={{ mb: 2 }}>
          <StyledFormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Rejection Reason</InputLabel>
            <Select
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              label="Rejection Reason"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                    backgroundColor: COLORS.background.secondary,
                    border: `1px solid ${COLORS.border.primary}`,
                  },
                },
              }}
            >
              {rejectionReasons.map((reason) => (
                <MenuItem
                  key={reason}
                  value={reason}
                  sx={{
                    color: COLORS.text.primary,
                    fontSize: { xs: "0.875rem", md: "1rem" },
                    "&:hover": {
                      backgroundColor: `${COLORS.primary.main}10`,
                    },
                    "&.Mui-selected": {
                      backgroundColor: `${COLORS.primary.main}20`,
                    },
                  }}
                >
                  {reason}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>

          {!rejectionReason && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Please select a rejection reason before proceeding.
            </Alert>
          )}
        </Box>
      )}

      {/* Additional Notes */}
      <StyledTextField
        fullWidth
        label={
          reviewAction === "reject"
            ? rejectionReason === "Custom reason (specify below)"
              ? "Custom Rejection Reason"
              : "Additional Notes (Optional)"
            : "Admin Notes (Optional)"
        }
        multiline
        rows={isMobile ? 2 : 3}
        value={customNotes}
        onChange={(e) => setCustomNotes(e.target.value)}
        placeholder={
          reviewAction === "reject"
            ? "Provide specific details about the rejection..."
            : "Add any notes for internal use..."
        }
        sx={{ mb: 2 }}
      />

      {reviewAction === "reject" &&
        rejectionReason === "Custom reason (specify below)" &&
        !customNotes.trim() && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Please provide a custom rejection reason.
          </Alert>
        )}
    </Box>
  );

  // Mobile Layout
  if (isMobile) {
    return (
      <StyledDrawer
        anchor="bottom"
        open={open}
        onClose={handleClose}
        disableSwipeToOpen
        BackdropProps={{
          sx: {
            top: "64px", // Start backdrop below navbar
            height: "calc(100vh - 64px)",
          },
        }}
        ModalProps={{
          keepMounted: false, // Better performance
          disablePortal: false,
        }}
      >
        <MobileContent>
          {/* Mobile Header */}
          <MobileHeader position="static">
            <Toolbar sx={{ minHeight: { xs: "56px" } }}>
              <Typography
                variant="h6"
                sx={{
                  color: COLORS.text.primary,
                  fontWeight: 700,
                  flex: 1,
                  fontSize: "1.1rem",
                }}
              >
                Review Video
              </Typography>
              <IconButton
                onClick={handleClose}
                disabled={submitting}
                sx={{ color: COLORS.text.secondary }}
              >
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </MobileHeader>

          {/* Video Player */}
          <VideoPlayer />

          {/* Tabs */}
          <Box sx={{ borderBottom: `1px solid ${COLORS.border.primary}` }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                "& .MuiTab-root": {
                  color: COLORS.text.secondary,
                  fontSize: "0.875rem",
                  minHeight: "48px",
                  textTransform: "none",
                },
                "& .Mui-selected": {
                  color: COLORS.primary.main,
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: COLORS.primary.main,
                },
              }}
            >
              <Tab
                label={
                  <TabLabel>
                    <InfoIcon fontSize="small" />
                    Info
                  </TabLabel>
                }
              />
              <Tab
                label={
                  <TabLabel>
                    <GavelIcon fontSize="small" />
                    Review
                  </TabLabel>
                }
              />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <MobileInfoSection>
            {activeTab === 0 && (
              <MobileTabPanel>
                <InfoContent />
              </MobileTabPanel>
            )}
            {activeTab === 1 && (
              <MobileTabPanel>
                <ReviewActions />
              </MobileTabPanel>
            )}
          </MobileInfoSection>

          {/* Mobile Action Buttons */}
          <ActionSection>
            <Button
              onClick={handleClose}
              disabled={submitting}
              sx={{
                color: COLORS.text.secondary,
                borderColor: COLORS.border.primary,
                flex: 1,
                "&:hover": {
                  borderColor: COLORS.border.focus,
                  backgroundColor: COLORS.hover.background,
                },
              }}
            >
              Cancel
            </Button>

            <ActionButton
              variant="reject"
              onClick={() => {
                if (reviewAction === "reject") {
                  handleReject();
                } else {
                  setReviewAction("reject");
                  setActiveTab(1); // Switch to review tab
                }
              }}
              disabled={
                submitting ||
                (reviewAction === "reject" &&
                  (!rejectionReason ||
                    (rejectionReason === "Custom reason (specify below)" &&
                      !customNotes.trim())))
              }
              startIcon={<CancelIcon />}
            >
              {reviewAction === "reject" ? "Confirm Reject" : "Reject"}
            </ActionButton>

            <ActionButton
              variant="approve"
              onClick={() => {
                if (reviewAction === "approve") {
                  handleApprove();
                } else {
                  setReviewAction("approve");
                  handleApprove();
                }
              }}
              disabled={submitting}
              startIcon={<CheckCircleIcon />}
            >
              {submitting ? "Processing..." : "Approve"}
            </ActionButton>
          </ActionSection>
        </MobileContent>
      </StyledDrawer>
    );
  }

  // Desktop Layout
  return (
    <StyledDialog open={open} onClose={handleClose} fullWidth maxWidth={false}>
      <StyledDialogContent>
        {/* Header */}
        <Header>
          <Typography
            variant="h5"
            sx={{
              color: COLORS.text.primary,
              fontWeight: 700,
              fontSize: { xs: "1.25rem", md: "1.5rem" },
            }}
          >
            Review Video Content
          </Typography>
          <IconButton
            onClick={handleClose}
            disabled={submitting}
            sx={{ color: COLORS.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </Header>

        {/* Main Content */}
        <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
          {/* Video Player Section */}
          <VideoPlayer />

          {/* Info Sidebar */}
          <Box
            sx={{
              width: { lg: "350px", xl: "400px" },
              display: "flex",
              flexDirection: "column",
              borderLeft: `1px solid ${COLORS.border.primary}`,
            }}
          >
            <InfoSection sx={{ flex: 1, overflow: "auto" }}>
              <InfoContent />

              <Divider sx={{ my: 3, borderColor: COLORS.border.primary }} />

              <ReviewActions />
            </InfoSection>

            {/* Action Buttons */}
            <ActionSection>
              <Button
                onClick={handleClose}
                disabled={submitting}
                sx={{
                  color: COLORS.text.secondary,
                  borderColor: COLORS.border.primary,
                  "&:hover": {
                    borderColor: COLORS.border.focus,
                    backgroundColor: COLORS.hover.background,
                  },
                }}
              >
                Cancel
              </Button>

              <ActionButton
                variant="reject"
                onClick={() => {
                  if (reviewAction === "reject") {
                    handleReject();
                  } else {
                    setReviewAction("reject");
                  }
                }}
                disabled={
                  submitting ||
                  (reviewAction === "reject" &&
                    (!rejectionReason ||
                      (rejectionReason === "Custom reason (specify below)" &&
                        !customNotes.trim())))
                }
                startIcon={<CancelIcon />}
              >
                {reviewAction === "reject" ? "Confirm Reject" : "Reject"}
              </ActionButton>

              <ActionButton
                variant="approve"
                onClick={() => {
                  if (reviewAction === "approve") {
                    handleApprove();
                  } else {
                    setReviewAction("approve");
                    handleApprove();
                  }
                }}
                disabled={submitting}
                startIcon={<CheckCircleIcon />}
              >
                {submitting ? "Processing..." : "Approve"}
              </ActionButton>
            </ActionSection>
          </Box>
        </Box>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default JobReviewModal;
